import { task } from 'mobx-task'
import Workspace from 'iam/models/Workspace'
import { action, computed, makeObservable, observable, reaction } from 'mobx'
import { AlertFactory, AlertState } from 'alerts/Alert'
import { CreateBundleParams } from 'admin/bundle/dtos/dtos'
import { BundleStore } from 'admin/bundle/BundleStore'
import WorkspaceSelectorViewModel from 'admin/bundle/WorkspaceSelectorForBundlesViewModel'
import InventoryStore from 'admin/InventoryStore'
import { RefreshBundles } from 'admin/bundle/BundleSearchViewModel'
import { mapIncludedItemVmToIds } from 'admin/bundle/dtos/bundleGrpcDtoMapper'
import { IWithWorkspaceSelectionForBundles } from 'admin/bundle/components/BundleFormWorkpaceDropdown'
import { required, validationContext } from 'validx'
import { Bundle } from 'admin/bundle/bundle'
import ModalViewModel from 'infra/ModalViewModel'
import ManageItemsInBundleViewModel, {
  IncludedItemViewModel,
} from 'admin/bundle/components/ManageItemsInBundleViewModel/ManageItemsInBundleViewModel'
import { PriceEditorViewModel } from '../PriceEditorViewModel'
import { lengthBetween } from 'utils/validation-utils'
import { Id } from 'types/definitions'

export default class DuplicateBundleViewModel
  extends ModalViewModel
  implements IWithWorkspaceSelectionForBundles
{
  constructor(
    public inventoryItemStore: InventoryStore,
    private bundleStore: BundleStore,
    private refreshBundles: RefreshBundles,
    public getSelectedWorkspace: () => Workspace | undefined,
    public workspaceSelectorVM: WorkspaceSelectorViewModel,
    private newAlert: AlertFactory,
  ) {
    super()
    makeObservable(this)

    this.close$.subscribe(this.reset)

    reaction(
      () => this.workspace,
      () => {
        this.refetchAssignedItems()
      },
    )
  }

  /**
   * The name for the Bundle.
   */
  @observable
  name = ''

  /**
   * The description for the Bundle.
   */
  @observable
  description = ''

  /**
   * The Bundle price.
   */
  @observable
  price = 0

  /**
   * The included items for the Bundle.
   */
  @observable
  includedItems: IncludedItemViewModel[] = []

  /**
   * The base inventory item id for the Bundle.
   */
  @observable
  baseItemId = ''

  /**
   * A selected Bundle. Used for edit/override operations.
   */
  @observable
  bundle: Bundle = Bundle.none

  /**
   * The selected workspace
   */
  @observable
  workspace? = Workspace.none

  manageItemsInBundleViewModel = new ManageItemsInBundleViewModel(
    this.setIncludedItems,
    this.inventoryItemStore,
  )

  /**
   * The Bundle price editor view model.
   */
  readonly pricingVM = new PriceEditorViewModel()

  /**
   * Holds the maximum lengths for each field.
   */
  private readonly fieldLengths = {
    name: { min: 1, max: 100 },
    description: { min: 1, max: 280 },
  } as const

  /**
   * Validations for the workspace
   */
  workspaceValidation = validationContext(this, {
    workspace: [(opts) => !!opts.value || 'Workspace is required'],
  })

  /**
   * Validation context for this view model.
   */
  validation = validationContext(this, {
    name: [
      required('A name is required.'),
      lengthBetween(
        this.fieldLengths.name.min,
        this.fieldLengths.name.max,
        `Name must be between ${this.fieldLengths.name.min} and ${this.fieldLengths.name.max} characters.`,
      ),
    ],
    description: [
      required('A description is required.'),
      lengthBetween(
        this.fieldLengths.description.min,
        this.fieldLengths.description.max,
        `Description must be between ${this.fieldLengths.description.min} and ${this.fieldLengths.description.max} characters.`,
      ),
    ],
  })

  /**
   * Computes the number of remaining characters for each field
   */
  @computed
  get remainingCharactersIndicator() {
    const makeIndicator = (max: number, current: number) =>
      `${max - current} characters remaining`

    return {
      name: makeIndicator(this.fieldLengths.name.max, this.name.length),
      description: makeIndicator(
        this.fieldLengths.description.max,
        this.description.length,
      ),
    }
  }

  /**
   * Returns a sorted copy of the included items list, ordered by their names.
   *
   * @return {Array} The sorted copy of included items, ordered by name.
   */
  @computed
  get orderedIncludedItems(): IncludedItemViewModel[] {
    const baseItem = this.includedItems.find((i) => i.isBaseItem)
    const allButBaseItem = this.includedItems
      .filter((i) => !i.isBaseItem)
      .sort((a, b) => a.name.localeCompare(b.name))

    if (baseItem) {
      return [baseItem, ...allButBaseItem]
    }

    return allButBaseItem
  }

  /**
   * Set the workspace for the new item
   */
  @action.bound
  setWorkspace(workspace?: Workspace) {
    this.workspace = workspace
    this.manageItemsInBundleViewModel.setWorkspaceId(this.workspace?.id || '')

    const filteredIncludedItems = this.includedItems.filter(
      (item) =>
        item.workspaceId === undefined ||
        item.workspaceId === this.workspace?.id,
    )

    this.manageItemsInBundleViewModel.setBaseItemId(
      filteredIncludedItems.find((i) => i.isBaseItem)?.inventoryItemId || '',
    )
    this.manageItemsInBundleViewModel.setCurrentlyIncludedItems(
      filteredIncludedItems,
    )
  }

  /**
   * Resets elements involved in the creation process
   */
  @action.bound
  reset() {
    this.name = ''
    this.description = ''
    this.price = 0
    this.includedItems = []
    this.baseItemId = ''
    this.manageItemsInBundleViewModel.baseItemId = ''
    this.manageItemsInBundleViewModel.includedItems = []

    this.submit.reset()
    this.validation.reset()
    this.pricingVM.reset()
  }

  @action.bound
  setBundle(bundle: Bundle) {
    this.bundle = bundle
    this.name = bundle.name
    this.description = bundle.description
    this.includedItems = bundle.includedItems.map(
      (i) =>
        new IncludedItemViewModel(
          true,
          i.isBaseItem,
          i.inventoryItemId,
          i.inventoryItemVersionId,
          i.name,
          i.price,
          i.proPrice,
          i.description,
          i.workspaceId,
          i.includedQuantity,
          i.archived,
        ),
    )
    this.price = bundle.price
    this.baseItemId =
      this.includedItems.find((item) => item.isBaseItem)?.inventoryItemId || ''

    this.manageItemsInBundleViewModel.setBaseItemId(this.baseItemId)
    this.manageItemsInBundleViewModel.setCurrentlyIncludedItems(
      this.includedItems,
    )

    this.pricingVM.hydrate(bundle.price)
  }

  @action
  open(bundle: Bundle) {
    this.reset()
    this.setWorkspace(bundle.workspace)
    this.setBundle(bundle)
    this.manageItemsInBundleViewModel.activate()
    this.show()
  }

  @action.bound
  setIncludedItems(includedItems: IncludedItemViewModel[]) {
    this.baseItemId =
      includedItems.find((item) => item.isBaseItem)?.inventoryItemId || ''
    this.manageItemsInBundleViewModel.setBaseItemId(this.baseItemId)
    this.includedItems = includedItems
  }

  @action.bound
  validate(): boolean {
    // Both should run independently.
    const base = this.validation.reset().validate().isValid
    const pricing = this.pricingVM.validate()

    return base && pricing
  }

  /**
   * Duplicates the bundle
   */
  submit = task.resolved(async () => {
    if (
      !this.validate() ||
      !this.workspaceValidation.reset().validate().isValid ||
      this.workspace === undefined
    ) {
      return
    }

    if (this.baseItemId === '') {
      this.newAlert()
        .withState(AlertState.Warning)
        .saying('Base item is required', 'Please include a base item.')
        .autoDismiss()
        .show()
      return
    }

    const includedItemsProto = this.includedItems.filter((i) => !i.isBaseItem)

    this.includedItems = includedItemsProto

    const price = this.pricingVM.toValue()

    const payload: CreateBundleParams = {
      name: this.name,
      description: this.description,
      baseItemId: this.baseItemId,
      includedItemsIds: mapIncludedItemVmToIds(
        this.includedItems.filter(
          (item) => item.inventoryItemId !== this.baseItemId,
        ),
      ),
      price,
      workspaceId: this.workspace.id!,
    }

    try {
      await this.bundleStore.create(payload)
    } catch (e) {
      this.newAlert()
        .withState(AlertState.Error)
        .saying('Error', e.message)
        .autoDismiss()
        .show()

      this.close()
      return
    }

    // Refresh the list to show new bundles
    await this.refreshBundles(this.workspace || Workspace.none)

    this.validation.reset()

    this.newAlert()
      .withState(AlertState.Success)
      .saying('Success', 'Bundle duplicated.')
      .autoDismiss()
      .show()

    this.close()
  })

  /**
   *  Refetch the assigned included items, since we need to check if we can still show the same included items.
   */
  refetchAssignedItems() {
    // Reassign the original base item and included items to the view model, so we can check if we can still show them.
    const baseItemId =
      this.bundle.includedItems.find((i) => i.isBaseItem)?.inventoryItemId || ''

    this.manageItemsInBundleViewModel.setBaseItemId(baseItemId)
    this.refetchAssignedIncludedItemsForDuplicating()
  }

  async refetchAssignedIncludedItemsForDuplicating() {
    if (!this.workspace) {
      return
    }

    const includedItemIds = this.includedItems.map((item) =>
      Id.assert(item.inventoryItemId),
    )
    const workspaceId = Id.assert(this.workspace.id)

    const includedItems =
      await this.inventoryItemStore.getManyInventoryItemsByIds(
        includedItemIds,
        workspaceId,
      )

    const filteredIncludedItems = includedItems.filter(
      (item) =>
        item.workspace === undefined || item.workspace.id === workspaceId,
    )

    // Some items won't be added to the new bundle because they are part of a different workspace
    if (filteredIncludedItems.length !== includedItemIds.length) {
      this.newAlert()
        .withState(AlertState.Warning)
        .saying(
          'Some service items could not be included in the duplicated bundle',
          'Please create the missing service items and add to bundle.',
        )
        .autoDismiss()
        .show()
    }

    const includedItemVms = filteredIncludedItems.map(
      (item) =>
        new IncludedItemViewModel(
          this.includedItems.find((i) => i.inventoryItemId === item.id)
            ?.isIncludedItem || false,
          item.id === this.baseItemId,
          item.id,
          item.versionId,
          item.name,
          item.price,
          item.proPrice,
          item.description,
          item.workspace?.id,
          this.includedItems.find((i) => i.inventoryItemId === item.id)
            ?.includedQuantity || 0,
          item.archived,
        ),
    )

    this.setIncludedItems(includedItemVms)
  }
}
